import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useTranslation } from 'react-i18next';
import type { Task, PertEstimate, SkillLevel, EstimateMode, Employee } from "../../types";
import { SKILL_LEVEL_COLORS, pertExpectedValue, pertStandardDeviation } from "../../types";
import { useEmployees, getNextTaskId, useSkills } from "../../stores";
import { X, ChevronDown, Calendar, ChevronLeft, ChevronRight, Eye, EyeOff, Trash2 } from "lucide-react";
import { skillsToEmojiString, parseEmojiToSkillCounts } from "../../lib/skillEngine";
import { evaluateTeamRequirement, analyzeTeamSkillGaps } from "../../lib/skillEngine";
import { EstimatesEditor, saveLastEstimates } from "./EstimatesEditor";
import { BOARD_START_DATE } from "../../lib/dateUtils";
import { CategoryMultiSelect } from "../../taskCategories/components/CategoryMultiSelect";
import { useModalDrag } from "../../hooks/useModalDrag";
import { UNIT_TO_DAYS } from "./EmployeeEstimateList";
import type { TimeUnit } from "./EmployeeEstimateList";
import type { ScheduledTask } from "../../boardv3/scheduler";
import { useCreateBlockNote } from '@blocknote/react';
import { BlockNoteView } from '@blocknote/mantine';
import type { BlockNoteEditor as BlockNoteEditorType } from '@blocknote/core';
import '@blocknote/mantine/style.css';

// ============================================
// Types
// ============================================

export type TaskModalMode = 'create' | 'edit';

export interface TaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (task: Task) => void;
  onUpdate?: (task: Task) => void;
  columns: string[];
  defaultColumn?: string;
  defaultStartDate?: string;
  // Mode & navigation
  mode: TaskModalMode;
  taskId?: string;
  taskHistory: string[];
  historyIndex: number;
  onNavigate?: (taskId: string, newIndex: number) => void;
  // For edit mode: resolve task by id
  getTaskById?: (taskId: string) => Task | undefined;
  // Scheduler data for calculations in edit mode
  getScheduledTask?: (taskId: string) => ScheduledTask | undefined;
  // Destructive actions for edit mode
  onDelete?: (taskId: string) => void;
  onRemoveFromBoard?: (taskId: string) => void;
}

// ============================================
// Helpers
// ============================================

function getMonthNamesShort(locale: string): string[] {
  const fmt = new Intl.DateTimeFormat(locale, { month: 'short' });
  return Array.from({ length: 12 }, (_, i) => fmt.format(new Date(2024, i, 1)));
}

function formatDateShort(dateStr: string, locale: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  if (isNaN(d.getTime())) return dateStr;
  const months = getMonthNamesShort(locale);
  return `${d.getDate()}. ${months[d.getMonth()]}`;
}

function getFixedPopoverStyle(btnRef: React.RefObject<HTMLButtonElement | null>): React.CSSProperties {
  const rect = btnRef.current?.getBoundingClientRect();
  return {
    position: 'fixed',
    top: rect ? rect.bottom + 4 : 0,
    left: rect ? rect.left : 0,
    backgroundColor: '#fff',
    borderRadius: 8,
    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
    border: '1px solid #e5e7eb',
    padding: 8,
    zIndex: 10000,
    minWidth: 180,
  };
}

/** Parse requiredSkills emoji string back to SkillLevel record */
function parseSkillsFromTask(requiredSkills: string | undefined | object): Record<string, SkillLevel> {
  if (!requiredSkills || typeof requiredSkills !== 'string') return {};
  const counts = parseEmojiToSkillCounts(requiredSkills);
  const result: Record<string, SkillLevel> = {};
  for (const [skillId, level] of Object.entries(counts)) {
    result[skillId] = Math.min(3, level) as SkillLevel;
  }
  return result;
}

// ============================================
// BlockNote Field for description/consequence
// ============================================

function TaskBlockNoteField({
  initialContent,
  placeholder,
  onEditorReady,
}: {
  initialContent: unknown[];
  placeholder: string;
  onEditorReady: (editor: BlockNoteEditorType) => void;
}) {
  const contentKey = useMemo(() => JSON.stringify(initialContent), [initialContent]);
  const editor = useCreateBlockNote({
    initialContent: initialContent.length > 0 ? (initialContent as any) : undefined,
  }, [contentKey]);

  useEffect(() => {
    onEditorReady(editor);
  }, [editor, onEditorReady]);

  return (
    <div data-task-bn-field className="text-xs [&_.bn-editor]:!text-xs [&_.bn-block-group]:!p-0 [&_.bn-block-outer]:!p-0 min-h-[32px]">
      <style>{`
[data-task-bn-field] .bn-editor { padding-inline: 4px !important; }
[data-task-bn-field] .bn-side-menu .mantine-UnstyledButton-root:not(.mantine-Menu-item) {
  background-color: white;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}
      `}</style>
      <BlockNoteView editor={editor} theme="light" data-placeholder={placeholder} />
    </div>
  );
}

// ============================================
// Berechnungen Section (from TaskDetailPanel)
// ============================================

function BerechnungenSection({
  task,
  employees,
  scheduled,
}: {
  task: Task;
  employees: Employee[];
  scheduled?: ScheduledTask;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [showSensitive, setShowSensitive] = useState(false);

  const assignees = task.assigneeIds
    .map((id) => employees.find((e) => e.id === id))
    .filter((e): e is Employee => e !== undefined);
  const primaryAssignee = assignees[0] || null;

  const skillMatch = task.requiredSkills && assignees.length > 0
    ? evaluateTeamRequirement(task.requiredSkills, assignees)
    : null;
  const skillGaps = task.requiredSkills && assignees.length > 0
    ? analyzeTeamSkillGaps(task.requiredSkills, assignees)
    : [];

  const contributionDetails = scheduled?.contributionDetails || [];
  const totalSpeed = scheduled?.totalSpeed || 0;
  const totalRisk = scheduled?.totalRisk || 0;
  const teamWeeks = scheduled?.teamWeeks || 0;
  const baseDays = scheduled?.baseDays;
  const effectiveDays = scheduled?.effectiveDays;

  if (assignees.length === 0) return null;

  return (
    <div className="border-t border-gray-100 pt-2">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1.5 text-[10px] text-gray-500 hover:text-gray-700 transition-colors w-full"
      >
        <ChevronDown className={`w-3 h-3 transition-transform ${expanded ? '' : '-rotate-90'}`} />
        <span>{t('task.calculations')}</span>
        {skillMatch && (
          <span className={`ml-1 ${skillMatch.matches ? 'text-emerald-600' : 'text-red-500'}`}>
            {skillMatch.matches ? '✓' : '✗'}
          </span>
        )}
        <div className="flex-1" />
        {expanded && (
          <span
            onClick={(e) => { e.stopPropagation(); setShowSensitive(!showSensitive); }}
            className={`p-0.5 rounded transition-colors ${showSensitive ? 'text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
            title={showSensitive ? t('task.hideSensitive') : t('task.showSensitive')}
          >
            {showSensitive ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
          </span>
        )}
      </button>

      {expanded && (
        <div className="mt-2 space-y-3">
          {/* Skill Match Summary (compact – full skill editor is above) */}
          {skillMatch && !skillMatch.matches && skillGaps.length > 0 && (
            <div className="flex items-center gap-1.5 text-[10px] text-red-600">
              <span>{t('task.skillsMissing')}</span>
              {skillGaps.map(g => (
                <span key={g.skillId} className="inline-flex items-center gap-0.5">
                  {g.emoji} {g.skillName}
                </span>
              ))}
            </div>
          )}

          {/* Contribution Details */}
          {assignees.length > 0 && (
            <div className="p-2 rounded border border-gray-200 bg-gray-50 text-xs">
              <div className="font-medium text-gray-700 mb-1.5">{t('task.teamAndCalculation')}</div>
              <div className="space-y-1">
                {contributionDetails.map((detail) => {
                  const emp = employees.find(e => e.id === detail.employeeId);
                  if (!emp) return null;
                  return (
                    <div key={emp.id} className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: emp.color }} />
                        <span>{emp.name}</span>
                      </div>
                      {showSensitive ? (
                        <div className="flex items-center gap-2 text-[10px] text-gray-500">
                          <span>{detail.pert.o}/{detail.pert.m}/{detail.pert.p}</span>
                          <span>E={detail.estimate.toFixed(1)}w</span>
                          <span className={`px-1 rounded ${
                            detail.risk > 0.8 ? 'bg-red-100 text-red-600' :
                            detail.risk > 0.5 ? 'bg-yellow-100 text-yellow-700' :
                            'bg-green-100 text-green-700'
                          }`}>
                            σ={detail.risk.toFixed(2)}
                          </span>
                          <span className="font-medium text-gray-700">{detail.contributionPercent.toFixed(1)}%</span>
                        </div>
                      ) : (
                        <span className="text-gray-400">•••</span>
                      )}
                    </div>
                  );
                })}
              </div>

              {showSensitive && (
                <>
                  <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-200 text-[10px]">
                    <span>{t('task.speed')}: <span className="font-bold">{totalSpeed.toFixed(3)}/w</span></span>
                    <span className={`px-1.5 rounded ${
                      totalRisk > 0.8 ? 'bg-red-100 text-red-600' :
                      totalRisk > 0.5 ? 'bg-yellow-100 text-yellow-700' :
                      'bg-green-100 text-green-700'
                    }`}>
                      {t('task.risk')}: σ={totalRisk.toFixed(2)}w
                    </span>
                  </div>

                  {baseDays && primaryAssignee && (
                    <div className="mt-2 pt-2 border-t border-gray-200 text-[10px]">
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <div className="text-gray-500">{t('task.alone', { name: primaryAssignee.name })}</div>
                          <div className="text-sm font-bold">{Math.ceil(baseDays / 5)}w <span className="text-[9px] text-gray-400 font-normal">({baseDays}d)</span></div>
                        </div>
                        <div>
                          <div className="text-gray-500">{t('task.withTeam')}</div>
                          <div className="text-sm font-bold text-green-600">{teamWeeks.toFixed(1)}w <span className="text-[9px] text-gray-400 font-normal">({effectiveDays}d)</span></div>
                        </div>
                      </div>
                      {assignees.length > 1 && (
                        <div className="mt-1.5 text-gray-600">
                          {t('task.timeSaving')}: <span className="font-bold text-green-600">{Math.round((1 - teamWeeks / (baseDays / 5)) * 100)}%</span>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* All Estimates (sorted) – hidden when individual estimates are shown in editor */}
          {showSensitive && task.estimateMode !== 'individual' && (
            <AllEstimatesSection task={task} employees={employees} />
          )}
        </div>
      )}
    </div>
  );
}

function AllEstimatesSection({ task, employees }: { task: Task; employees: Employee[] }) {
  const { t } = useTranslation();
  const sortedEmployees = [...employees]
    .filter(emp => {
      const est = task.estimates[emp.id];
      return est !== null && est !== undefined;
    })
    .sort((a, b) => {
      const aEst = task.estimates[a.id];
      const bEst = task.estimates[b.id];
      const aVal = aEst ? pertExpectedValue(aEst) : 99;
      const bVal = bEst ? pertExpectedValue(bEst) : 99;
      return aVal - bVal;
    });

  if (sortedEmployees.length === 0) return null;

  return (
    <div className="p-2 rounded border border-gray-200 bg-gray-50 text-xs">
      <div className="font-medium text-gray-700 mb-1.5">{t('task.allEstimates')}</div>
      <div className="space-y-0.5">
        {sortedEmployees.map(emp => {
          const isAssigned = task.assigneeIds.includes(emp.id);
          const pert = task.estimates[emp.id];
          if (!pert) return null;
          const expected = pertExpectedValue(pert);
          const risk = pertStandardDeviation(pert);
          return (
            <div key={emp.id} className={`flex items-center justify-between py-0.5 px-1 rounded ${isAssigned ? 'bg-blue-50' : ''}`}>
              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: emp.color }} />
                <span>{emp.name}</span>
              </div>
              <div className="flex items-center gap-1.5 text-[10px] text-gray-500">
                <span>{pert.o}/{pert.m}/{pert.p}</span>
                <span className={isAssigned ? 'text-blue-700 font-bold' : ''}>{expected.toFixed(1)}w</span>
                <span className={`px-1 rounded ${
                  risk > 0.8 ? 'bg-red-100 text-red-600' :
                  risk > 0.5 ? 'bg-yellow-100 text-yellow-700' :
                  'bg-green-100 text-green-700'
                }`}>
                  ±{risk.toFixed(1)}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================
// TaskModal Component
// ============================================

export function TaskModal({
  isOpen,
  onClose,
  onAdd,
  onUpdate,
  columns,
  defaultColumn,
  defaultStartDate,
  mode,
  taskId,
  taskHistory,
  historyIndex,
  onNavigate,
  getTaskById,
  getScheduledTask,
  onDelete,
  onRemoveFromBoard,
}: TaskModalProps) {
  const { t, i18n } = useTranslation();
  const employees = useEmployees();
  const skillDefinitions = useSkills();
  const { modalRef, position, isDragging, handleMouseDown, handleDoubleClick } = useModalDrag();

  const [title, setTitle] = useState("");
  const [column, setColumn] = useState(defaultColumn || columns[0] || "");
  const [selectedDate, setSelectedDate] = useState(defaultStartDate || BOARD_START_DATE);
  const [estimateMode, setEstimateMode] = useState<EstimateMode>('default');
  const [defaultEstimate, setDefaultEstimate] = useState<PertEstimate>({ o: 5, m: 10, p: 20 });

  useEffect(() => { if (defaultColumn) setColumn(defaultColumn); }, [defaultColumn]);
  useEffect(() => { if (defaultStartDate) setSelectedDate(defaultStartDate); }, [defaultStartDate]);

  const [categoryIds, setCategoryIds] = useState<string[]>([]);
  const [descContent, setDescContent] = useState<unknown[]>([]);
  const [consContent, setConsContent] = useState<unknown[]>([]);
  const [editorKey, setEditorKey] = useState(0);
  const descEditorRef = useRef<BlockNoteEditorType | null>(null);
  const consEditorRef = useRef<BlockNoteEditorType | null>(null);
  const [selectedSkills, setSelectedSkills] = useState<Record<string, SkillLevel>>({});
  const [estimates, setEstimates] = useState<Record<string, PertEstimate>>({});
  const [assigneeIds, setAssigneeIds] = useState<string[]>([]);

  // Track which task is currently loaded in edit mode
  const [loadedTaskId, setLoadedTaskId] = useState<string | null>(null);

  // Popover states
  const [columnDropdownOpen, setColumnDropdownOpen] = useState(false);
  const [datePopoverOpen, setDatePopoverOpen] = useState(false);

  // Estimate state
  const [estimateUnit, setEstimateUnit] = useState<TimeUnit>('weeks');
  const [usePert, setUsePert] = useState(false);

  // Refs for trigger buttons
  const columnBtnRef = useRef<HTMLButtonElement>(null);
  const dateBtnRef = useRef<HTMLButtonElement>(null);

  // Refs for popover panels
  const columnPanelRef = useRef<HTMLDivElement>(null);
  const datePanelRef = useRef<HTMLDivElement>(null);

  const anyPopoverOpen = columnDropdownOpen || datePopoverOpen;

  const handleAddEstimate = (employeeId: string) => {
    setEstimates((prev) => ({ ...prev, [employeeId]: { o: 10, m: 10, p: 10 } }));
  };
  const handleRemoveEstimate = (employeeId: string) => {
    setEstimates((prev) => { const { [employeeId]: _, ...rest } = prev; return rest; });
    setAssigneeIds((prev) => prev.filter((id) => id !== employeeId));
  };
  const handleToggleAssignee = (employeeId: string) => {
    setAssigneeIds((prev) => prev.includes(employeeId) ? prev.filter((id) => id !== employeeId) : [...prev, employeeId]);
  };
  const handleUpdateEstimate = (employeeId: string, estimate: PertEstimate) => {
    setEstimates((prev) => ({ ...prev, [employeeId]: estimate }));
  };
  const handleSkillChange = (skillId: string, level: SkillLevel) => {
    setSelectedSkills((prev) => ({ ...prev, [skillId]: level }));
  };

  const handlePertToggle = useCallback(() => {
    if (usePert) {
      setDefaultEstimate(prev => ({ o: prev.m, m: prev.m, p: prev.m }));
    }
    setUsePert(!usePert);
  }, [usePert]);

  const titleInputRef = useRef<HTMLInputElement>(null);
  const [createdToast, setCreatedToast] = useState<string | null>(null);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout>>(null);

  const resetForm = useCallback(() => {
    setTitle(""); setDescContent([]); setConsContent([]);
    setCategoryIds([]); setSelectedSkills({});
    setEstimates({}); setAssigneeIds([]); setEstimateMode('default');
    setDefaultEstimate({ o: 5, m: 10, p: 20 });
    setColumnDropdownOpen(false); setDatePopoverOpen(false);
    setEstimateUnit('weeks'); setUsePert(false);
    setLoadedTaskId(null);
    descEditorRef.current = null;
    consEditorRef.current = null;
    setEditorKey(k => k + 1);
  }, []);

  // Load task data into form for edit mode
  const loadTaskIntoForm = useCallback((tId: string) => {
    if (!getTaskById) return;
    const task = getTaskById(tId);
    if (!task) return;

    setTitle(task.title);
    setColumn(task.column || columns[0] || "");
    setSelectedDate(task.startDate || BOARD_START_DATE);
    setEstimateMode(task.estimateMode || 'default');
    setDefaultEstimate(task.defaultEstimate || { o: 5, m: 10, p: 20 });
    setCategoryIds(task.categoryIds ?? []);
    setDescContent(Array.isArray(task.description) ? task.description : []);
    setConsContent(Array.isArray(task.consequence) ? task.consequence : []);
    descEditorRef.current = null;
    consEditorRef.current = null;
    setEditorKey(k => k + 1);
    setSelectedSkills(parseSkillsFromTask(task.requiredSkills as string | undefined));
    // Filter out null estimates for the editor
    const cleanEstimates: Record<string, PertEstimate> = {};
    for (const [k, v] of Object.entries(task.estimates)) {
      if (v) cleanEstimates[k] = v;
    }
    setEstimates(cleanEstimates);
    setAssigneeIds([...task.assigneeIds]);
    setColumnDropdownOpen(false);
    setDatePopoverOpen(false);
    setLoadedTaskId(tId);

    // Detect if PERT values differ
    if (task.defaultEstimate) {
      const de = task.defaultEstimate;
      setUsePert(de.o !== de.m || de.m !== de.p);
    } else {
      setUsePert(false);
    }
  }, [getTaskById, columns]);

  // Build current task from form state (for save/update)
  const buildCurrentTask = useCallback((): Task | undefined => {
    if (!title.trim()) return undefined;
    const requiredSkillsEmoji = skillsToEmojiString(selectedSkills);
    const descDoc = descEditorRef.current?.document as unknown[] | undefined;
    const consDoc = consEditorRef.current?.document as unknown[] | undefined;
    return {
      id: loadedTaskId || '',
      title: title.trim(),
      column,
      startDate: selectedDate,
      estimateMode,
      categoryIds,
      ...(estimateMode === 'default' && { defaultEstimate }),
      estimates,
      assigneeIds,
      ...(descDoc && descDoc.length > 0 && { description: descDoc }),
      ...(consDoc && consDoc.length > 0 && { consequence: consDoc }),
      ...(requiredSkillsEmoji && { requiredSkills: requiredSkillsEmoji }),
    };
  }, [title, column, selectedDate, estimateMode, defaultEstimate, categoryIds, selectedSkills, estimates, assigneeIds, loadedTaskId]);

  // Save current edit-mode task
  const saveCurrentEdit = useCallback(() => {
    if (mode !== 'edit' || !loadedTaskId || !onUpdate) return;
    const task = buildCurrentTask();
    if (task) onUpdate(task);
  }, [mode, loadedTaskId, onUpdate, buildCurrentTask]);

  // Load task when entering edit mode or when taskId changes
  useEffect(() => {
    if (!isOpen) return;
    if (mode === 'edit' && taskId && taskId !== loadedTaskId) {
      loadTaskIntoForm(taskId);
    }
    if (mode === 'create' && loadedTaskId) {
      // Switching from edit back to create (end of history)
      resetForm();
      setColumn(defaultColumn || columns[0] || "");
      setSelectedDate(defaultStartDate || BOARD_START_DATE);
      titleInputRef.current?.focus();
    }
  }, [isOpen, mode, taskId, loadedTaskId, loadTaskIntoForm, resetForm, defaultColumn, columns, defaultStartDate]);

  // Navigation handlers
  const canGoBack = historyIndex > 0 || (mode === 'create' && taskHistory.length > 0);
  const canGoForward = mode === 'edit' && historyIndex < taskHistory.length - 1;
  const canGoForwardToCreate = mode === 'edit' && historyIndex === taskHistory.length - 1;

  const navigateBack = useCallback(() => {
    if (!onNavigate) return;
    if (mode === 'edit' && historyIndex > 0) {
      saveCurrentEdit();
      const prevIndex = historyIndex - 1;
      onNavigate(taskHistory[prevIndex], prevIndex);
    } else if (mode === 'create' && taskHistory.length > 0) {
      // From create form, go back to last created task
      const prevIndex = taskHistory.length - 1;
      onNavigate(taskHistory[prevIndex], prevIndex);
    }
  }, [mode, historyIndex, taskHistory, onNavigate, saveCurrentEdit]);

  const navigateForward = useCallback(() => {
    if (!onNavigate) return;
    if (mode === 'edit') {
      saveCurrentEdit();
      if (historyIndex < taskHistory.length - 1) {
        const nextIndex = historyIndex + 1;
        onNavigate(taskHistory[nextIndex], nextIndex);
      }
    }
  }, [mode, historyIndex, taskHistory, onNavigate, saveCurrentEdit]);

  // Create-mode submit
  const handleSubmit = (): Task | undefined => {
    if (!title.trim()) return;
    if (!column) return;
    const hasIndividualEst = Object.keys(estimates).length > 0;
    if (estimateMode === 'individual' && !hasIndividualEst) return;
    const requiredSkillsEmoji = skillsToEmojiString(selectedSkills);
    const descDoc = descEditorRef.current?.document as unknown[] | undefined;
    const consDoc = consEditorRef.current?.document as unknown[] | undefined;
    const task: Task = {
      id: getNextTaskId(), title: title.trim(), column, startDate: selectedDate,
      estimateMode, categoryIds,
      ...(estimateMode === 'default' && { defaultEstimate }),
      estimates, assigneeIds,
      ...(descDoc && descDoc.length > 0 && { description: descDoc }),
      ...(consDoc && consDoc.length > 0 && { consequence: consDoc }),
      ...(requiredSkillsEmoji && { requiredSkills: requiredSkillsEmoji }),
    };
    if (hasIndividualEst) saveLastEstimates(estimates);
    onAdd(task);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setCreatedToast(task.title);
    toastTimerRef.current = setTimeout(() => setCreatedToast(null), 2500);
    return task;
  };

  const handleSubmitAndContinue = () => {
    const task = handleSubmit();
    if (!task) return;
    resetForm();
    setColumn(defaultColumn || columns[0] || "");
    setSelectedDate(defaultStartDate || BOARD_START_DATE);
    titleInputRef.current?.focus();
  };

  const handleSubmitAndClose = () => {
    const task = handleSubmit();
    if (!task) return;
    resetForm();
    setColumn(columns[0] || "");
    setSelectedDate(defaultStartDate || BOARD_START_DATE);
    setCreatedToast(null);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    onClose();
  };

  const handleClose = useCallback(() => {
    if (mode === 'edit') {
      saveCurrentEdit();
    }
    resetForm();
    setColumn(columns[0] || "");
    setSelectedDate(defaultStartDate || BOARD_START_DATE);
    setCreatedToast(null);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    onClose();
  }, [mode, saveCurrentEdit, resetForm, columns, defaultStartDate, onClose]);

  const handleDeleteTask = useCallback(() => {
    if (!loadedTaskId || !onDelete) return;
    if (!confirm(t('task.deleteGlobalConfirm'))) return;
    onDelete(loadedTaskId);
    resetForm();
    onClose();
  }, [loadedTaskId, onDelete, resetForm, onClose]);

  const handleRemoveFromBoard = useCallback(() => {
    if (!loadedTaskId || !onRemoveFromBoard) return;
    onRemoveFromBoard(loadedTaskId);
    resetForm();
    onClose();
  }, [loadedTaskId, onRemoveFromBoard, resetForm, onClose]);

  const closeAllPopovers = () => {
    setColumnDropdownOpen(false);
    setDatePopoverOpen(false);
  };

  // Keyboard handling
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (anyPopoverOpen) {
          closeAllPopovers();
        } else {
          handleClose();
        }
        return;
      }

      // Cmd/Ctrl+Enter
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        if (mode === 'create') {
          handleSubmitAndContinue();
        } else {
          handleClose();
        }
        return;
      }

      // Enter in edit mode (not in input/textarea/contenteditable) → save + close
      if (e.key === 'Enter' && mode === 'edit') {
        const tag = (e.target as HTMLElement).tagName;
        const isContentEditable = (e.target as HTMLElement).isContentEditable;
        if (tag !== 'INPUT' && tag !== 'TEXTAREA' && tag !== 'SELECT' && !isContentEditable) {
          handleClose();
          return;
        }
      }

      // Arrow navigation - only when no input/textarea/contenteditable focused
      const tag = (e.target as HTMLElement).tagName;
      const isContentEditable = (e.target as HTMLElement).isContentEditable;
      const isInput = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || isContentEditable;
      if (!isInput) {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          navigateBack();
          return;
        }
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          if (canGoForward) {
            navigateForward();
          } else if (canGoForwardToCreate && onNavigate) {
            // Navigate to create form at end of history
            saveCurrentEdit();
            onNavigate('__create__', taskHistory.length);
          }
          return;
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  });

  // Click-outside for popovers
  useEffect(() => {
    if (!anyPopoverOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (columnDropdownOpen && columnBtnRef.current && !columnBtnRef.current.contains(target) && columnPanelRef.current && !columnPanelRef.current.contains(target)) {
        setColumnDropdownOpen(false);
      }
      if (datePopoverOpen && dateBtnRef.current && !dateBtnRef.current.contains(target) && datePanelRef.current && !datePanelRef.current.contains(target)) {
        setDatePopoverOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [anyPopoverOpen, columnDropdownOpen, datePopoverOpen]);

  if (!isOpen) return null;

  const hasIndividualEst = Object.keys(estimates).length > 0;
  const isValid = title.trim() && column && (estimateMode === 'default' || hasIndividualEst);

  const toDisplay = (days: number) => Math.round((days / UNIT_TO_DAYS[estimateUnit]) * 100) / 100;
  const toDays = (value: number) => Math.round(value * UNIT_TO_DAYS[estimateUnit]);

  const showNavArrows = taskHistory.length > 0 || (mode === 'edit');

  // Get scheduled data for current task in edit mode
  const currentScheduled = mode === 'edit' && loadedTaskId && getScheduledTask
    ? getScheduledTask(loadedTaskId)
    : undefined;

  // Build a materialized task for the Berechnungen display
  const editTaskForCalc: Task | undefined = mode === 'edit' && loadedTaskId
    ? buildCurrentTask()
    : undefined;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-[10001]" onClick={handleClose} />
      <div className="fixed inset-0 z-[10002] flex items-center justify-center p-4 pointer-events-none">
        <div
          ref={modalRef}
          className={`bg-white rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] flex flex-col ${position ? '' : 'pointer-events-auto'}`}
          style={position ? { position: 'fixed', left: position.x, top: position.y, pointerEvents: 'auto' } : undefined}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between px-4 py-2 border-b border-gray-100 shrink-0"
            onMouseDown={handleMouseDown}
            onDoubleClick={handleDoubleClick}
            style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
          >
            <div className="flex items-center gap-2">
              {showNavArrows && (
                <div className="flex items-center gap-0.5">
                  <button
                    onClick={(e) => { e.stopPropagation(); navigateBack(); }}
                    onMouseDown={e => e.stopPropagation()}
                    disabled={!canGoBack}
                    className="p-0.5 rounded text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    title={t('task.prevTask')}
                  >
                    <ChevronLeft className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (canGoForward) {
                        navigateForward();
                      } else if (canGoForwardToCreate && onNavigate) {
                        saveCurrentEdit();
                        onNavigate('__create__', taskHistory.length);
                      }
                    }}
                    onMouseDown={e => e.stopPropagation()}
                    disabled={!canGoForward && !canGoForwardToCreate}
                    className="p-0.5 rounded text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    title={t('task.nextTask')}
                  >
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
              <span className="text-xs font-medium text-gray-500">
                {mode === 'create' ? t('task.newTask') : (loadedTaskId || t('task.editTask'))}
              </span>
            </div>
            <button onClick={handleClose} onMouseDown={e => e.stopPropagation()} className="text-gray-400 hover:text-gray-600">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Content */}
          <div className="px-4 py-3 space-y-3 overflow-y-auto flex-1">
            {/* Title - hero style */}
            <input
              ref={titleInputRef}
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !anyPopoverOpen && mode === 'create') {
                  e.preventDefault();
                  handleSubmitAndContinue();
                }
              }}
              placeholder={t('task.titlePlaceholder')}
              className="w-full text-sm font-medium text-gray-900 placeholder-gray-400 bg-transparent border-none outline-none focus:ring-0"
              style={{ padding: 0 }}
              autoFocus
            />

            {/* Description - BlockNote editor */}
            <div>
              <span className="text-[10px] text-gray-400">{t('task.description')}</span>
              <TaskBlockNoteField
                key={`desc-${editorKey}`}
                initialContent={descContent}
                placeholder={t('task.descriptionPlaceholder')}
                onEditorReady={(ed) => { descEditorRef.current = ed; }}
              />
            </div>

            {/* Consequence - BlockNote editor */}
            <div>
              <span className="text-[10px] text-gray-400">{t('task.consequence')}</span>
              <TaskBlockNoteField
                key={`cons-${editorKey}`}
                initialContent={consContent}
                placeholder={t('task.consequencePlaceholder')}
                onEditorReady={(ed) => { consEditorRef.current = ed; }}
              />
            </div>

            {/* Metadata Chip Toolbar */}
            <div className="flex flex-wrap items-center gap-2">
              {/* Column Chip */}
              <div>
                <button
                  ref={columnBtnRef}
                  type="button"
                  onClick={() => { closeAllPopovers(); setColumnDropdownOpen(!columnDropdownOpen); }}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-gray-200 bg-gray-50 text-xs font-medium text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  {column || 'Column'}
                  <ChevronDown className="w-3 h-3 text-gray-400" />
                </button>
              </div>

              {/* Date Chip */}
              <div>
                <button
                  ref={dateBtnRef}
                  type="button"
                  onClick={() => { closeAllPopovers(); setDatePopoverOpen(!datePopoverOpen); }}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-gray-200 bg-gray-50 text-xs font-medium text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  <Calendar className="w-3 h-3 text-gray-400" />
                  {formatDateShort(selectedDate, i18n.language)}
                  <ChevronDown className="w-3 h-3 text-gray-400" />
                </button>
              </div>

              {/* Categories Chip */}
              <CategoryMultiSelect selectedCategoryIds={categoryIds} onChange={setCategoryIds} allowInlineCreate={true} compact={true} />
            </div>

            {/* Estimate Section */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-gray-500">{t('task.estimate')}</span>
                <div className="flex gap-1">
                  <button type="button" onClick={() => setEstimateMode('default')}
                    className={`px-2 py-0.5 text-[10px] rounded border transition-colors ${estimateMode === 'default' ? 'bg-blue-50 text-blue-700 border-blue-300' : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'}`}
                  >{t('task.estimateModeTask')}</button>
                  <button type="button" onClick={() => setEstimateMode('individual')}
                    className={`px-2 py-0.5 text-[10px] rounded border transition-colors ${estimateMode === 'individual' ? 'bg-blue-50 text-blue-700 border-blue-300' : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'}`}
                  >{t('task.estimateModeIndividual')}</button>
                </div>
              </div>

              {/* Default estimate: PERT toggle + inputs + unit */}
              {estimateMode === 'default' && (
                <div className="flex items-center gap-2">
                  <button type="button" onClick={handlePertToggle}
                    className={`px-2 py-0.5 text-[10px] rounded border transition-colors ${usePert ? 'bg-blue-600 text-white border-blue-600' : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'}`}
                  >PERT</button>
                  {usePert ? (
                    <>
                      <div className="flex items-center gap-1">
                        <span className="text-[9px] text-green-600">O</span>
                        <input type="number" min="0.1" step="0.5" value={toDisplay(defaultEstimate.o)}
                          onChange={(e) => setDefaultEstimate(prev => ({ ...prev, o: toDays(Number(e.target.value)) }))}
                          className="w-14 px-1.5 py-0.5 text-[10px] border border-gray-200 rounded bg-white text-gray-900 focus:ring-1 focus:ring-blue-500" />
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-[9px] text-blue-600">M</span>
                        <input type="number" min="0.1" step="0.5" value={toDisplay(defaultEstimate.m)}
                          onChange={(e) => setDefaultEstimate(prev => ({ ...prev, m: toDays(Number(e.target.value)) }))}
                          className="w-14 px-1.5 py-0.5 text-[10px] border border-gray-200 rounded bg-white text-gray-900 focus:ring-1 focus:ring-blue-500" />
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-[9px] text-orange-600">P</span>
                        <input type="number" min="0.1" step="0.5" value={toDisplay(defaultEstimate.p)}
                          onChange={(e) => setDefaultEstimate(prev => ({ ...prev, p: toDays(Number(e.target.value)) }))}
                          className="w-14 px-1.5 py-0.5 text-[10px] border border-gray-200 rounded bg-white text-gray-900 focus:ring-1 focus:ring-blue-500" />
                      </div>
                    </>
                  ) : (
                    <input type="number" min="0.1" step="0.5" value={toDisplay(defaultEstimate.m)}
                      onChange={(e) => {
                        const days = toDays(Number(e.target.value));
                        setDefaultEstimate({ o: days, m: days, p: days });
                      }}
                      className="w-14 px-1.5 py-0.5 text-[10px] border border-gray-200 rounded bg-white text-gray-900 focus:ring-1 focus:ring-blue-500" />
                  )}
                  <select value={estimateUnit} onChange={(e) => setEstimateUnit(e.target.value as TimeUnit)}
                    className="px-2 py-0.5 text-[10px] rounded border border-gray-200 bg-gray-50 text-gray-700">
                    {Object.entries({ days: t('estimates.days'), weeks: t('estimates.weeks'), months: t('estimates.months') } as Record<string, string>).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                  </select>
                </div>
              )}

              {/* Individual estimates */}
              {estimateMode === 'individual' && (
                <EstimatesEditor
                  employees={employees} estimates={estimates} assigneeIds={assigneeIds}
                  requiredSkills={selectedSkills}
                  onAdd={handleAddEstimate} onUpdate={handleUpdateEstimate}
                  onRemove={handleRemoveEstimate} onToggleAssignee={handleToggleAssignee}
                />
              )}
            </div>

            {/* Compact Skills */}
            {skillDefinitions.length > 0 && (
              <div>
                <span className="text-[10px] text-gray-500 mb-1 block">{t('task.requiredSkills')}</span>
                <div className="flex flex-wrap gap-1">
                  {skillDefinitions.map((skillDef) => {
                    const level = (selectedSkills[skillDef.id] ?? 0) as SkillLevel;
                    return (
                      <div key={skillDef.id} className="group relative inline-flex items-center gap-0.5 px-1 py-0.5 rounded bg-gray-50 border border-gray-100">
                        <span className="text-[10px]">{skillDef.emoji}</span>
                        {([1, 2, 3] as SkillLevel[]).map((l) => (
                          <button
                            key={l}
                            type="button"
                            onClick={() => handleSkillChange(skillDef.id, level === l ? 0 : l)}
                            className={`w-4 h-4 rounded text-[8px] leading-none transition-all ${
                              level >= l ? SKILL_LEVEL_COLORS[l] + ' border' : 'bg-white text-gray-300 hover:bg-gray-100'
                            }`}
                          >
                            {l}
                          </button>
                        ))}
                        <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-1.5 py-0.5 rounded bg-gray-800 text-white text-[9px] whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-50">
                          {skillDef.name}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Berechnungen section (edit-mode only) */}
            {mode === 'edit' && editTaskForCalc && (
              <BerechnungenSection
                task={editTaskForCalc}
                employees={employees}
                scheduled={currentScheduled}
              />
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-2 border-t border-gray-100 flex items-center justify-between shrink-0">
            {mode === 'create' ? (
              <>
                <div className="flex items-center gap-2 min-w-0">
                  {createdToast ? (
                    <span className="text-[10px] text-green-600 truncate max-w-[200px]">
                      {t('task.created', { title: createdToast })}
                    </span>
                  ) : (
                    <span className="text-[10px] text-gray-400">ESC · ⌘Enter</span>
                  )}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button
                    onClick={handleSubmitAndClose}
                    disabled={!isValid}
                    className="px-2.5 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {t('task.createAndClose')}
                  </button>
                  <button
                    onClick={handleSubmitAndContinue}
                    disabled={!isValid}
                    className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('task.createAndNext')}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  {onDelete && loadedTaskId && (
                    <button
                      onClick={handleDeleteTask}
                      className="inline-flex items-center gap-1 px-2 py-1.5 text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
                    >
                      <Trash2 className="w-3 h-3" />
                      {t('common.delete')}
                    </button>
                  )}
                  {onRemoveFromBoard && loadedTaskId && (
                    <button
                      onClick={handleRemoveFromBoard}
                      className="px-2 py-1.5 text-xs font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                    >
                      {t('task.deleteFromBoard')}
                    </button>
                  )}
                </div>
                <button
                  onClick={handleClose}
                  className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
                >
                  {t('common.save')}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Popovers rendered outside modal to avoid overflow clipping */}
      {columnDropdownOpen && (
        <div ref={columnPanelRef} style={getFixedPopoverStyle(columnBtnRef)}>
          {columns.map((col) => (
            <button
              key={col}
              type="button"
              onClick={() => { setColumn(col); setColumnDropdownOpen(false); }}
              className={`w-full text-left px-2.5 py-1.5 text-xs rounded hover:bg-gray-100 transition-colors ${col === column ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'}`}
            >
              {col}
            </button>
          ))}
        </div>
      )}

      {datePopoverOpen && (
        <div ref={datePanelRef} style={getFixedPopoverStyle(dateBtnRef)}>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => { setSelectedDate(e.target.value); setDatePopoverOpen(false); }}
            className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-gray-900 bg-white"
            autoFocus
          />
        </div>
      )}
    </>
  );
}

// Backwards-compatible wrapper
export function AddTaskModal(props: {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (task: Task) => void;
  columns: string[];
  defaultColumn?: string;
  defaultStartDate?: string;
}) {
  return (
    <TaskModal
      {...props}
      mode="create"
      taskHistory={[]}
      historyIndex={-1}
    />
  );
}
